import { useEffect, useState } from 'react';

/**
 * Returns `true` once `active` has stayed continuously true for `delayMs`.
 *
 * Used to surface a "check your internet connection" hint when a results
 * loader runs longer than expected — almost always a slow / dropped network
 * on the user's side. The flag resets to false the instant `active` goes
 * false (results arrived, or the request errored out), and the pending timer
 * is cleared so a fast subsequent load never shows a stale warning.
 *
 * @param active   whether the thing we're waiting on is currently loading
 * @param delayMs  how long to wait before flagging it as slow (default 5s)
 * @param resetKey bump this when a NEW load starts while `active` never went
 *                 false (search B fired while search A was still in flight).
 *                 Without it the timer never restarts and the new load is
 *                 branded slow from its very first millisecond.
 */
export function useSlowLoadWarning(active: boolean, delayMs = 5000, resetKey: unknown = 0): boolean {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (!active) {
      setSlow(false);
      return;
    }
    setSlow(false);
    const t = setTimeout(() => setSlow(true), delayMs);
    return () => clearTimeout(t);
  }, [active, delayMs, resetKey]);

  return slow;
}
